/**
 * O contexto unico do aplicativo.
 *
 * Tudo o que as telas precisam de saber sobre a plataforma passa por aqui: o
 * nucleo, o estado de sincronizacao, o assistente e o modo leitura-apenas.
 * Nao ha aqui nada de dominio nenhum (regra 1) — so a plataforma.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CoreEvents, type PluginManifest, type Scope, type SessionInfo, type SyncStatus } from '@flos/shared';
import type { BootResult } from '../boot';
import type { ReadOnlyState } from '../wiring/ports';

export interface AppContextValue {
  boot: BootResult;
  /** Manifestos visiveis a esta pessoa, ja filtrados por permissoes. */
  manifests: PluginManifest[];
  can(scope: Scope): boolean;
  readOnly: ReadOnlyState | null;
  sync: SyncStatus;
  /** Sessao ativa, ou `null` enquanto ninguem entrou. */
  session: SessionInfo | null;
  /** Volta a ler o estado do disco (usado depois de escrever). */
  refreshSync(): Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export interface AppProviderProps {
  boot: BootResult;
  children: ReactNode;
}

export function AppProvider({ boot, children }: AppProviderProps) {
  const [sync, setSync] = useState<SyncStatus>(() => boot.sync.status());
  // Trocar de sessao muda as permissoes, logo muda as rotas e os widgets
  // visiveis. Sem isto, entrar no fim do onboarding deixava a tela vazia.
  const [sessionTick, setSessionTick] = useState(0);

  useEffect(() => boot.sync.subscribe(setSync), [boot.sync]);

  useEffect(
    () => boot.core.events.on(CoreEvents.SESSION_CHANGED, () => setSessionTick((n) => n + 1)),
    [boot.core],
  );

  const manifests = useMemo(
    () => boot.core.registry.manifests(),
    // `sessionTick` e a dependencia a serio: o registro filtra por permissoes.
    [boot.core, sessionTick],
  );

  const session = useMemo(() => boot.core.session.peek(), [boot.core, sessionTick]);

  const can = useCallback(
    (scope: Scope) => boot.core.session.can(scope),
    [boot.core, sessionTick],
  );

  const refreshSync = useCallback(() => boot.sync.refresh(), [boot.sync]);

  const value = useMemo<AppContextValue>(
    () => ({
      boot,
      manifests,
      can,
      readOnly: boot.readOnly,
      sync,
      session,
      refreshSync,
    }),
    [boot, manifests, can, sync, session, refreshSync],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp() precisa de um <AppProvider> acima na árvore.');
  }
  return context;
}

/** Atalho usado por quase todos as telas. */
export function useCore() {
  return useApp().boot.core;
}
